import { useState, useCallback } from "react";
import { getOptionText } from "@util/utils";
import { INVOICE_CARRIER_TYPE_OPTIONS, INVOICE_ISSUING_TYPE_OPTIONS } from "@icoach/pos/PosScreenPage.config";

/**
 * 發票狀態管理 Hook
 * 統一管理發票相關的狀態和文字轉換
 *
 * @param {Object} initialData - 初始發票資料
 * @param {Object} options - options 物件，包含發票類型選項等
 * @returns {Object} 包含發票狀態和操作方法的物件
 */
export const useInvoiceState = (initialData = null, options) => {
	const [invoiceData, setInvoiceData] = useState(initialData);

	const { invoiceTypeOption = [], invoiceCarrierTypeOption = [] } = options;

	const issuingTypeOptions = invoiceTypeOption.length > 0 ? invoiceTypeOption : INVOICE_ISSUING_TYPE_OPTIONS;
	const carrierTypeOptions = invoiceCarrierTypeOption.length > 0 ? invoiceCarrierTypeOption : INVOICE_CARRIER_TYPE_OPTIONS;

	/**
	 * 更新發票資料並自動轉換文字
	 * @param {Object} newData - 新的發票資料
	 * @returns {Object} 包含文字欄位的完整發票資料
	 */
	const updateInvoiceData = useCallback(
		newData => {
			if (!newData) {
				setInvoiceData(null);
				return null;
			}

			const enrichedData = {
				...newData,
				// 自動添加文字欄位
				issuingTypeText: newData.issuingTypeText || getOptionText(issuingTypeOptions, newData.issuingType),
				invoiceCarrierTypeText: newData.invoiceCarrierTypeText || getOptionText(carrierTypeOptions, newData.carrierType),
			};

			setInvoiceData(enrichedData);
			return enrichedData;
		},
		// eslint-disable-next-line
		[]
	);

	/**
	 * 重置發票資料
	 */
	const resetInvoiceData = useCallback(
		() => {
			setInvoiceData(null);
		},
		// eslint-disable-next-line
		[]
	);

	/**
	 * 更新發票資料的特定欄位
	 * @param {Object} partialData - 要更新的部分資料
	 */
	const updatePartialInvoiceData = useCallback(
		partialData => {
			if (!invoiceData) {
				updateInvoiceData(partialData);
				return;
			}

			const updatedData = {
				...invoiceData,
				...partialData,
				// 如果更新了 issuingType 或 carrierType，重新計算文字欄位
				issuingTypeText: partialData.issuingType !== undefined ? getOptionText(issuingTypeOptions, partialData.issuingType) : invoiceData.issuingTypeText,
				invoiceCarrierTypeText: partialData.carrierType !== undefined ? getOptionText(carrierTypeOptions, partialData.carrierType) : invoiceData.invoiceCarrierTypeText,
			};

			setInvoiceData(updatedData);
		},
		// eslint-disable-next-line
		[invoiceData, updateInvoiceData]
	);

	return {
		invoiceData,
		updateInvoiceData,
		updatePartialInvoiceData,
		resetInvoiceData,
	};
};

export default useInvoiceState;
